import type { LazyQueryExecFunction, LazyQueryHookExecOptions } from '@apollo/client';
import { memo } from 'react';

import type {
  GetMacroQuery,
  GetMacroQueryVariables,
  GetTemplateQuery,
  GetTemplateQueryVariables,
} from 'generated/graphql';
import type { ReportWorklistItem } from 'hooks/useCurrentCaseReport';
import { useRecoilValue } from 'recoil';
import { ReportStatus, reportStatusState, REPORT_EDITABLE_STATUSES } from '../state';
import { RightDrawer } from './RightDrawer';
import { RIGHT_DRAWER_WIDTH } from './constants';
import { RightDrawerWrapper } from './RightDrawerWrapper';
import { useRightDrawerResizable } from './useRightDrawer';

type ReporterRightDrawerProps = {
  worklistItem?: ReportWorklistItem;
  onLoadTemplate?: LazyQueryExecFunction<GetTemplateQuery, GetTemplateQueryVariables>;
  onLoadMacro?: (
    options: Partial<LazyQueryHookExecOptions<GetMacroQuery, GetMacroQueryVariables>>
  ) => Promise<void>;
  isReporterLocked: boolean;
  showDiscardReportUI: boolean;
  showDraftReportUI: boolean;
  showPrelimReportUI: boolean;
};

export const ReporterRightDrawer = memo(
  ({
    worklistItem,
    onLoadTemplate,
    onLoadMacro,
    isReporterLocked,
    showDiscardReportUI,
    showDraftReportUI,
    showPrelimReportUI,
  }: ReporterRightDrawerProps): React.ReactElement | null => {
    const [rightDrawerState] = useRightDrawerResizable();
    const status = useRecoilValue(reportStatusState);
    const isSubmittedStatus =
      status === ReportStatus.ProvisionalSubmit || status === ReportStatus.Submitted;
    const isReportEditableStatus = REPORT_EDITABLE_STATUSES.includes(
      status as (typeof REPORT_EDITABLE_STATUSES)[number]
    );

    if (rightDrawerState?.drawerHeight == null || worklistItem?.procedure == null) {
      return null;
    }

    return (
      <RightDrawerWrapper initialSize={RIGHT_DRAWER_WIDTH}>
        <RightDrawer.Root>
          <RightDrawer.TabSection
            drawerHeight={rightDrawerState.drawerHeight}
            onLoadTemplate={onLoadTemplate}
            onLoadMacro={onLoadMacro}
            procedureSmid={worklistItem.procedure.smid}
            disabledDoubleClick={
              isReporterLocked ||
              !isReportEditableStatus ||
              showDiscardReportUI ||
              showDraftReportUI ||
              showPrelimReportUI
            }
            isSubmittedStatus={isSubmittedStatus}
          />
          <RightDrawer.PreviewOrPicklistSection />
        </RightDrawer.Root>
      </RightDrawerWrapper>
    );
  }
);

ReporterRightDrawer.displayName = 'ReporterRightDrawer';
